import { siX, siInstagram, siFacebook, siTiktok, siYoutube, siGoogle } from 'simple-icons';
import { cn } from '@/lib/utils';
import { PLATFORMS, type Platform } from '@/lib/platforms';

// Official brand marks, so a network is recognisable at a glance instead of
// by a colored dot. Glyphs come from `simple-icons`; it dropped LinkedIn (and
// never carried Google Business Profile), so LinkedIn's path is inlined and
// GBP borrows the Google mark — same set as the landing LogoStrip.
const LINKEDIN_PATH =
  'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z';

const GLYPHS: Record<Platform, string> = {
  x: siX.path,
  instagram: siInstagram.path,
  facebook: siFacebook.path,
  linkedin: LINKEDIN_PATH,
  tiktok: siTiktok.path,
  youtube: siYoutube.path,
  gmb: siGoogle.path,
};

// Tile fills. Instagram's mark is its gradient, not a flat pink.
const TILE_BACKGROUND: Partial<Record<Platform, string>> = {
  instagram: 'linear-gradient(45deg, #F58529 0%, #DD2A7B 50%, #8134AF 75%, #515BD4 100%)',
};

// Black-on-black marks: in the glyph variant they follow the text color so
// they stay visible in dark mode.
const MONOCHROME: ReadonlySet<Platform> = new Set(['x', 'tiktok']);

const SIZES = {
  xs: { box: 'h-4 w-4 rounded-[4px]', glyph: 'h-2.5 w-2.5', bare: 'h-3.5 w-3.5' },
  sm: { box: 'h-5 w-5 rounded-[5px]', glyph: 'h-3 w-3', bare: 'h-4 w-4' },
  md: { box: 'h-7 w-7 rounded-[7px]', glyph: 'h-4 w-4', bare: 'h-5 w-5' },
  lg: { box: 'h-9 w-9 rounded-[10px]', glyph: 'h-5 w-5', bare: 'h-6 w-6' },
} as const;

export type PlatformIconSize = keyof typeof SIZES;

interface PlatformIconProps {
  /** Platform key (`'instagram'`, `'x'`…). Unknown keys render a neutral tile. */
  platform: string;
  size?: PlatformIconSize;
  /**
   * `tile` (default): white mark on the brand-colored square — use where the
   * icon identifies an account or item. `glyph`: the bare mark in brand color,
   * for inline use next to text.
   */
  variant?: 'tile' | 'glyph';
  /**
   * Set when the platform name is not shown next to the icon, so screen
   * readers still get it. Otherwise the icon is decorative.
   */
  labelled?: boolean;
  className?: string;
}

function isPlatform(p: string): p is Platform {
  return p in PLATFORMS;
}

/** Brand logo for a social platform. */
export function PlatformIcon({ platform, size = 'sm', variant = 'tile', labelled = false, className }: PlatformIconProps) {
  const s = SIZES[size];
  const a11y = labelled
    ? { role: 'img' as const, 'aria-label': isPlatform(platform) ? PLATFORMS[platform].label : platform }
    : { 'aria-hidden': true as const };

  if (!isPlatform(platform)) {
    return (
      <span
        {...a11y}
        className={cn(
          'inline-flex shrink-0 items-center justify-center bg-tertiary text-[10px] font-semibold uppercase text-muted-foreground ring-1 ring-inset ring-border',
          s.box,
          className
        )}
      >
        {platform.slice(0, 1)}
      </span>
    );
  }

  const glyph = (
    <svg viewBox="0 0 24 24" className={variant === 'tile' ? s.glyph : s.bare} aria-hidden>
      <path d={GLYPHS[platform]} fill="currentColor" />
    </svg>
  );

  if (variant === 'glyph') {
    return (
      <span
        {...a11y}
        className={cn('inline-flex shrink-0', MONOCHROME.has(platform) && 'text-foreground', className)}
        style={MONOCHROME.has(platform) ? undefined : { color: PLATFORMS[platform].color }}
      >
        {glyph}
      </span>
    );
  }

  return (
    <span
      {...a11y}
      className={cn(
        // The hairline keeps the black X / TikTok tiles visible on dark surfaces.
        'inline-flex shrink-0 items-center justify-center ring-1 ring-inset ring-white/10',
        s.box,
        className
      )}
      style={{ background: TILE_BACKGROUND[platform] ?? PLATFORMS[platform].color, color: PLATFORMS[platform].textColor }}
    >
      {glyph}
    </span>
  );
}
